/**
 * Pure helpers for translating Sanity portable-text arrays. Shared by:
 *
 *   • the Studio "Translate from English" button
 *     (`src/lib/sanity/components/TranslateFromEnButton.tsx`)
 *   • the document-level "Translate all missing spans" button
 *     (`src/lib/sanity/components/BulkTranslateMissingButton.tsx`)
 *   • the auto-translate webhook that fills every locale on first publish
 *     (`src/app/api/webhooks/sanity/auto-translate/route.ts`, via
 *     `buildTranslationPatch` in `translateDocument.ts`)
 *
 * No React / Sanity-client imports live here — these functions take plain
 * objects and return plain objects so they work in any runtime (client,
 * edge, node).
 *
 * ## Nested localePortableText
 *
 * A body block array does NOT only hold `_type: 'block'` prose. Tutorials
 * and CAS wrap their steps in `chapter` blocks whose `content` is itself a
 * `localePortableText`, and that content holds `stepByStepGrid` blocks whose
 * `steps[].stepInstructions` / `.additionalComment` are ALSO
 * `localePortableText`. (Annotated-image `annotations` are another example.)
 *
 * These functions recurse into every nested `localePortableText` at any
 * depth so that translating a body also translates the chapter prose and the
 * step instructions/comments, not just the top-level spans.
 *
 * ### Where the translations are stored
 *
 * The whole translated tree is written into the TARGET-language sub-array of
 * the FIELD (`body.<lang>`), mirroring the English structure with each nested
 * `localePortableText` carrying its own translated `.<lang>`. The public
 * renderer resolves the body with `pickPortableText(body, locale)` and then
 * resolves each nested field the same way at its own depth (see
 * `portable-text-renderer.tsx`), so it reads `body.ja → content.ja →
 * step.stepInstructions.ja` all the way down.
 *
 * `extractMissingSpans` therefore walks the English SOURCE and the current
 * TARGET (`body.<lang>`) in parallel: top-level prose blocks align by
 * `_key` first (position-fallback for legacy imports — see
 * `alignTextBlocks`), and nested structural members (chapters, step grids,
 * steps) align by `_key`, so an already-translated nested field is read from
 * the target tree and left untouched — the pass is idempotent and never
 * re-flags or clobbers a step that's already translated. Extraction and
 * re-application walk in the identical order, so each translated chunk lands
 * back in the exact nested field it came from.
 */

export type PortableSpan = {
  _type: 'span'
  _key?: string
  text?: string
  marks?: string[]
}

export type PortableBlock = {
  _type: string
  _key?: string
  children?: PortableSpan[]
  [k: string]: unknown
}

/**
 * Is this span worth sending to DeepL? Returns false for spans whose text
 * contains no letters at all — URLs, numbers, punctuation runs, empty
 * marker spans. DeepL returns those unchanged, which would cause the
 * "missing span" detector to flag them forever: the target still reads
 * like English because it literally IS the same string.
 */
export function isTranslatableSpanText(text: string): boolean {
  if (!text || !text.trim()) return false
  // \p{L} = any Unicode letter. If there's at least one, it's translatable.
  return /\p{L}/u.test(text)
}

/**
 * Inline HTML that has no business being inside a portable-text span. Emphasis
 * and links live in `marks` / `markDefs`, so a tag here is always malformed
 * input — historically from an imperfect WP→portable-text conversion.
 */
const INLINE_HTML =
  /<\/?(?:b|strong|i|em|u|a|br|span|p|div|sub|sup|small|font)\b[^>]*>/gi

/**
 * Strip inline HTML from a source span before it is sent for translation.
 *
 * The span text we hand DeepL is plain text by contract (`tag_handling=html`
 * is deliberately off — see `deepl.ts`), so a tag that sneaks into the English
 * source is translated as literal characters and written verbatim into every
 * target locale. One malformed English span therefore becomes nine malformed
 * translations, each of which renders the tag as visible text because the
 * portable-text renderer escapes span content.
 *
 * That is exactly how `<b>Members Only</b>` and a set of `<a href=…>` links
 * ended up displayed as markup across ru/ja/ko/pt/zh. Stripping here bounds
 * the blast radius to the one English span that is already wrong, instead of
 * multiplying it across the language set.
 *
 * `<br>` becomes a newline rather than vanishing, so an author's line break
 * survives the round trip.
 */
export function stripInlineHtml(text: string): string {
  if (!text.includes('<')) return text
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(INLINE_HTML, '')
}

/** True when a source span carries inline HTML that `stripInlineHtml` removes. */
export function hasInlineHtml(text: string): boolean {
  INLINE_HTML.lastIndex = 0
  return INLINE_HTML.test(text) || /<br\s*\/?>/i.test(text)
}

/**
 * A localePortableText value is a plain object keyed by locale id whose
 * per-locale value is an array of blocks (e.g. `{ _type, en: [...], ja: [...] }`).
 * Detect it so the recursion can translate `.en` → `.<lang>` in place while
 * leaving every other structural field (images, refs, `localeString`
 * captions, plain strings) untouched.
 */
function isLocalePortableText(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const o = v as Record<string, unknown>
  if (o._type === 'localePortableText') return true
  // Legacy values that predate the explicit `_type`: a bare object with an
  // `en` block array and no competing `_type` marker.
  return o._type == null && Array.isArray(o.en)
}

/**
 * A localeString / localeText value: a plain object keyed by locale id whose
 * per-locale value is a STRING — the caption-class fields embedded in a body
 * (videoBlock.title/caption, chapter.title, bonusPhoto.caption, imageGrid
 * image alt/caption, annotatedPhoto.label, riskItem.title). The recursion
 * translates its `.en` → `.<lang>` the same way it does span text, so these
 * fields stop being English-only.
 */
function isLocaleScalar(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const o = v as Record<string, unknown>
  if (o._type === 'localeString' || o._type === 'localeText') return true
  // Keyless legacy shape: a bare object with an `en` STRING and no `_type`.
  return o._type == null && typeof o.en === 'string'
}

/** The `_key` of a Sanity array member, or null if it has none. */
function keyOf(el: unknown): string | null {
  if (el && typeof el === 'object' && !Array.isArray(el)) {
    const k = (el as Record<string, unknown>)._key
    if (typeof k === 'string') return k
  }
  return null
}

/**
 * A unit of translation work over an arbitrary value: the texts to send to
 * DeepL plus a rebuild that, given exactly those translated chunks back in
 * order, returns the value with its nested translations applied.
 */
type NestedJob = {
  texts: string[]
  rebuild: (translated: string[]) => unknown
}

/** Combine per-child jobs of an array into one job that rebuilds the array. */
function combineArray(children: unknown[], childJobs: NestedJob[]): NestedJob {
  const counts = childJobs.map((j) => j.texts.length)
  return {
    texts: childJobs.flatMap((j) => j.texts),
    rebuild: (translated) => {
      let offset = 0
      return children.map((_, i) => {
        const slice = translated.slice(offset, offset + counts[i])
        offset += counts[i]
        return childJobs[i].rebuild(slice)
      })
    },
  }
}

/** Combine per-field jobs of an object into one job that rebuilds the object. */
function combineObject(
  entries: Array<[string, unknown]>,
  childJobs: NestedJob[]
): NestedJob {
  const counts = childJobs.map((j) => j.texts.length)
  return {
    texts: childJobs.flatMap((j) => j.texts),
    rebuild: (translated) => {
      let offset = 0
      const out: Record<string, unknown> = {}
      entries.forEach(([key], i) => {
        const slice = translated.slice(offset, offset + counts[i])
        offset += counts[i]
        out[key] = childJobs[i].rebuild(slice)
      })
      return out
    },
  }
}

// ---------------------------------------------------------------------------
// FULL translate (extractSpanTexts) — regenerate a language from English,
// overwriting the target. Nested localePortableText is always translated
// fresh (there is no "current" to preserve in overwrite mode).
// ---------------------------------------------------------------------------

/** Walk a non-block member and translate every nested localePortableText fresh. */
function collectNestedFull(value: unknown, lang: string): NestedJob {
  if (isLocalePortableText(value)) {
    const lpt = value as Record<string, unknown>
    const en = Array.isArray(lpt.en) ? (lpt.en as PortableBlock[]) : null
    if (!en || en.length === 0) return { texts: [], rebuild: () => value }
    const core = collectAllInBlocks(en, lang)
    if (core.texts.length === 0) return { texts: [], rebuild: () => value }
    return {
      texts: core.texts,
      rebuild: (t) => ({ ...lpt, [lang]: core.rebuild(t) }),
    }
  }
  if (isLocaleScalar(value)) {
    const lo = value as Record<string, unknown>
    const en = typeof lo.en === 'string' ? stripInlineHtml(lo.en) : ''
    if (!en || !isTranslatableSpanText(en)) return { texts: [], rebuild: () => value }
    return { texts: [en], rebuild: (t) => ({ ...lo, [lang]: t[0] ?? en }) }
  }
  if (Array.isArray(value)) {
    return combineArray(
      value,
      value.map((el) => collectNestedFull(el, lang))
    )
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    return combineObject(
      entries,
      entries.map(([, v]) => collectNestedFull(v, lang))
    )
  }
  return { texts: [], rebuild: () => value }
}

/** Per-source-member rebuild plan: how many texts it owns + how to rebuild it. */
type MemberPlan = { count: number; rebuild: (slice: string[]) => PortableBlock }

/** Turn a list of member plans + the flat text array into the rebuilt block array. */
function rebuildFromPlans(
  plans: MemberPlan[],
  translated: string[]
): PortableBlock[] {
  let offset = 0
  return plans.map((plan) => {
    const slice = translated.slice(offset, offset + plan.count)
    offset += plan.count
    return plan.rebuild(slice)
  })
}

/**
 * FULL-translate a block array for `lang`: every top-level span AND every
 * span inside every nested localePortableText is translated.
 */
function collectAllInBlocks(
  source: PortableBlock[],
  lang: string
): { texts: string[]; rebuild: (translated: string[]) => PortableBlock[] } {
  const texts: string[] = []
  const plans: MemberPlan[] = []

  for (const member of source) {
    if (member?._type === 'block' && Array.isArray(member.children)) {
      const children = member.children
      const sliceIdxByChild = new Map<number, number>()
      let localCount = 0
      children.forEach((child, childIdx) => {
        if (child?._type === 'span' && typeof child.text === 'string') {
          // Never hand inline HTML to the translator — see stripInlineHtml.
          const enText = stripInlineHtml(child.text)
          // Skip spans with no letters (URLs, numbers, punctuation runs):
          // DeepL returns them unchanged, so sending them only burns quota.
          // The rebuild keeps the English text for skipped spans — the same
          // result DeepL would have produced. Mirrors the missing-mode
          // filter below.
          if (!isTranslatableSpanText(enText)) return
          sliceIdxByChild.set(childIdx, localCount++)
          texts.push(enText)
        }
      })
      plans.push({
        count: localCount,
        rebuild: (slice) => ({
          ...member,
          children: children.map((c, childIdx) => {
            if (c?._type !== 'span') return { ...c }
            const si = sliceIdxByChild.get(childIdx)
            return si == null ? { ...c } : { ...c, text: slice[si] ?? c.text }
          }),
        }),
      })
    } else {
      const nested = collectNestedFull(member, lang)
      plans.push({
        count: nested.texts.length,
        rebuild: (slice) => nested.rebuild(slice) as PortableBlock,
      })
      texts.push(...nested.texts)
    }
  }

  return { texts, rebuild: (translated) => rebuildFromPlans(plans, translated) }
}

/**
 * Walk a portable-text array and collect every span's text into a flat
 * list, returning a rebuild function that injects translated strings
 * back into the same positions.
 *
 * Non-block members (images, separators, enhancedImage, etc.) are copied
 * structurally, EXCEPT that any nested `localePortableText` inside them
 * (chapter `content`, step `stepInstructions` / `additionalComment`,
 * annotated-image `annotations`, …) is translated into `lang` in place —
 * see the module header. `lang` is the target language whose nested
 * sub-arrays get written. This is the OVERWRITE path (regenerate from
 * English); use `extractMissingSpans` to fill only untranslated spans.
 */
export function extractSpanTexts(
  blocks: PortableBlock[],
  lang: string
): {
  texts: string[]
  rebuild: (translated: string[]) => PortableBlock[]
} {
  return collectAllInBlocks(blocks, lang)
}

// ---------------------------------------------------------------------------
// MISSING-only translate (extractMissingSpans) — fill untranslated spans,
// preserving prior machine/human output. Source and target are walked in
// parallel so nested fields read their existing translation from the target.
// ---------------------------------------------------------------------------

/**
 * Align the current target body's text blocks to the English source's.
 *
 * PRIMARY: match by `_key`. Our rebuild spreads the English block
 * (including its `_key`) into the target, so any translation produced by
 * this pipeline pairs exactly — even after the editor inserts, deletes,
 * or reorders English paragraphs. This is the fix for the silent
 * one-block-shift corruption pure positional alignment caused: inserting
 * a paragraph used to pair every subsequent translation with the wrong
 * English block, and the misaligned text (differing from its new English
 * partner) read as "already translated" and was copied into the wrong slot.
 *
 * FALLBACK: positional pairing applies ONLY when key matching found
 * nothing at all — the signature of a legacy TRP import whose keys never
 * matched the source (the reason position-alignment existed). In an array
 * where at least one key matched, the target is pipeline-managed: an
 * unmatched English block is NEW content and must align to `undefined`
 * so its spans are flagged and translated fresh. Pairing it with a
 * leftover target block (an orphaned deleted-paragraph translation, or a
 * translator's manually-added block) instead grafts the WRONG translation
 * onto it — permanently, because the differing text then reads as
 * "already translated" and no missing-only pass ever re-flags it. That
 * delete-a-paragraph-write-a-new-one edit is exactly the corruption class
 * this rewrite exists to kill.
 *
 * Non-text members (separators, images, enhancedImage) are filtered on
 * both sides *before* alignment, so structural drift in non-text items
 * doesn't shift anything.
 */
function alignTextBlocks(
  source: PortableBlock[],
  current: PortableBlock[] | undefined
): Array<PortableBlock | undefined> {
  const isTextBlock = (b: PortableBlock) =>
    b?._type === 'block' && Array.isArray(b.children)
  const enBlocks = source.filter(isTextBlock)
  const langBlocks = (current || []).filter(isTextBlock)

  const langByKey = new Map<string, PortableBlock>()
  for (const b of langBlocks) {
    const k = keyOf(b)
    if (k != null && !langByKey.has(k)) langByKey.set(k, b)
  }

  const aligned: Array<PortableBlock | undefined> = new Array(enBlocks.length)
  const claimed = new Set<PortableBlock>()
  enBlocks.forEach((b, i) => {
    const k = keyOf(b)
    const match = k != null ? langByKey.get(k) : undefined
    if (match && !claimed.has(match)) {
      aligned[i] = match
      claimed.add(match)
    }
  })

  // Legacy-only positional fallback — see the doc comment above.
  if (claimed.size === 0) {
    for (let i = 0; i < aligned.length; i++) {
      aligned[i] = langBlocks[i]
    }
  }
  return aligned
}

/**
 * Align a target block's span children to the English block's, per the
 * same key-primary / legacy-only-position-fallback strategy as
 * `alignTextBlocks`. Returns a map from the English child INDEX (raw
 * index into `children`, including non-span members) to the matched
 * target span, so the caller's flag/rebuild passes can look up by the
 * index they already iterate with. The positional fallback (Nth span ↔
 * Nth span) applies only when NO key matched — a key-matched block is
 * pipeline-managed, and an unmatched English span there is new content
 * that must be flagged fresh, never grafted onto a leftover span.
 */
function alignChildSpans(
  enChildren: PortableSpan[],
  targetChildren: Array<{ _type?: string; _key?: string; text?: string }>
): Map<number, { _type?: string; _key?: string; text?: string }> {
  const targetSpans = targetChildren.filter((c) => c?._type === 'span')
  const targetByKey = new Map<string, (typeof targetSpans)[number]>()
  for (const s of targetSpans) {
    if (typeof s._key === 'string' && !targetByKey.has(s._key)) {
      targetByKey.set(s._key, s)
    }
  }

  const out = new Map<number, (typeof targetSpans)[number]>()
  const claimed = new Set<(typeof targetSpans)[number]>()
  const enSpanIdx: number[] = []

  enChildren.forEach((child, childIdx) => {
    if (child?._type !== 'span') return
    enSpanIdx.push(childIdx)
    const k = typeof child._key === 'string' ? child._key : null
    const match = k != null ? targetByKey.get(k) : undefined
    if (match && !claimed.has(match)) {
      out.set(childIdx, match)
      claimed.add(match)
    }
  })

  // Legacy-only positional fallback: only when nothing key-matched.
  if (claimed.size === 0) {
    enSpanIdx.forEach((childIdx, spanOrdinal) => {
      if (targetSpans[spanOrdinal]) out.set(childIdx, targetSpans[spanOrdinal])
    })
  }
  return out
}

/** Mutable running count of spans that were already translated (diagnostic). */
type AlreadyAcc = { n: number }

/**
 * Walk a non-block source member together with its target-tree counterpart,
 * translating only the untranslated spans in every nested localePortableText.
 * Structural array members (steps, grid rows, chapters) are matched by `_key`
 * so the pass survives reordering; each nested field's existing translation
 * is read from the aligned target node, never from the English source.
 */
function collectNestedMissing(
  src: unknown,
  cur: unknown,
  lang: string,
  already: AlreadyAcc
): NestedJob {
  if (isLocalePortableText(src)) {
    const lpt = src as Record<string, unknown>
    const en = Array.isArray(lpt.en) ? (lpt.en as PortableBlock[]) : null
    if (!en || en.length === 0) return { texts: [], rebuild: () => src }
    const curLpt = isLocalePortableText(cur)
      ? (cur as Record<string, unknown>)
      : undefined
    const curArr =
      curLpt && Array.isArray(curLpt[lang])
        ? (curLpt[lang] as PortableBlock[])
        : undefined
    const core = collectMissingInBlocks(en, curArr, lang)
    already.n += core.translatedAlready
    if (core.texts.length === 0) {
      // Nothing missing — keep the English source structure but carry over
      // the existing target translation (if any) so it's preserved verbatim.
      return {
        texts: [],
        rebuild: () => (curArr ? { ...lpt, [lang]: curArr } : lpt),
      }
    }
    return {
      texts: core.texts,
      rebuild: (t) => ({ ...lpt, [lang]: core.rebuild(t) }),
    }
  }
  if (isLocaleScalar(src)) {
    const lo = src as Record<string, unknown>
    const en = typeof lo.en === 'string' ? stripInlineHtml(lo.en) : ''
    if (!en || !isTranslatableSpanText(en)) return { texts: [], rebuild: () => src }
    const curLo =
      cur && typeof cur === 'object' && !Array.isArray(cur)
        ? (cur as Record<string, unknown>)
        : undefined
    // Prefer the target's existing value; fall back to any translation already
    // on the source locale object. A present value is preserved verbatim.
    const existing =
      curLo && typeof curLo[lang] === 'string' && curLo[lang]
        ? (curLo[lang] as string)
        : typeof lo[lang] === 'string' && lo[lang]
          ? (lo[lang] as string)
          : undefined
    if (existing) {
      already.n += 1
      return { texts: [], rebuild: () => ({ ...lo, [lang]: existing }) }
    }
    return { texts: [en], rebuild: (t) => ({ ...lo, [lang]: t[0] ?? en }) }
  }
  if (Array.isArray(src)) {
    const curArr = Array.isArray(cur) ? (cur as unknown[]) : []
    const curByKey = new Map<string, unknown>()
    curArr.forEach((el) => {
      const k = keyOf(el)
      if (k != null) curByKey.set(k, el)
    })
    const childJobs = src.map((el, i) => {
      const k = keyOf(el)
      // A KEYED source element (a step, chapter, grid row) matches its
      // target strictly by key: when the key is absent from the target,
      // the element is NEW and must be treated as fresh (undefined), not
      // paired with whatever sits at the same index — positional pairing
      // there grafts a deleted sibling's translation onto new content.
      // Positional fallback is reserved for keyless legacy elements.
      const curEl = k != null ? curByKey.get(k) : curArr[i]
      return collectNestedMissing(el, curEl, lang, already)
    })
    return combineArray(src, childJobs)
  }
  if (src && typeof src === 'object') {
    const entries = Object.entries(src as Record<string, unknown>)
    const curObj =
      cur && typeof cur === 'object' && !Array.isArray(cur)
        ? (cur as Record<string, unknown>)
        : undefined
    const childJobs = entries.map(([key, v]) =>
      collectNestedMissing(v, curObj ? curObj[key] : undefined, lang, already)
    )
    return combineObject(entries, childJobs)
  }
  return { texts: [], rebuild: () => src }
}

/**
 * MISSING-only translate for a single block array. Shared core of
 * `extractMissingSpans` — recurses into nested localePortableText the same
 * way, reading each nested field's current translation from the aligned
 * target node so a step that's already translated is not re-flagged.
 */
function collectMissingInBlocks(
  source: PortableBlock[],
  current: PortableBlock[] | undefined,
  lang: string
): {
  texts: string[]
  rebuild: (translated: string[]) => PortableBlock[]
  translatedAlready: number
} {
  const alignedTargets = alignTextBlocks(source, current)
  // Non-block structural members align by `_key` against the target array.
  const currentByKey = new Map<string, PortableBlock>()
  for (const m of current || []) {
    const k = keyOf(m)
    if (k != null) currentByKey.set(k, m as PortableBlock)
  }

  const texts: string[] = []
  const plans: MemberPlan[] = []
  const already: AlreadyAcc = { n: 0 }

  // Only `_type: 'block'` members participate in text-block alignment;
  // nested localePortableText lives inside non-block members and aligns
  // by `_key`.
  let textBlockIdx = 0

  for (const member of source) {
    if (member?._type === 'block' && Array.isArray(member.children)) {
      const children = member.children
      const targetBlock = alignedTargets[textBlockIdx]
      const targetChildren = Array.isArray(targetBlock?.children)
        ? (targetBlock!.children as Array<{
            _type?: string
            _key?: string
            text?: string
          }>)
        : []
      // Span-level alignment (key-primary, span-ordinal fallback) — the
      // flag pass and the rebuild pass MUST read the same mapping or a
      // preserved span could land in a different slot than it was
      // compared in.
      const alignedSpanByChildIdx = alignChildSpans(children, targetChildren)

      // Block-local slice index for each missing child span.
      const localSliceByChild = new Map<number, number>()
      let localCount = 0
      children.forEach((child, childIdx) => {
        if (child?._type !== 'span' || typeof child.text !== 'string') return
        // Strip before every downstream use: what we send to the translator
        // and what we compare against the existing target must be the same
        // string, or a span that only differs by stripped markup would be
        // re-translated on every pass.
        const enText = stripInlineHtml(child.text)
        if (!isTranslatableSpanText(enText)) return
        const targetSpan = alignedSpanByChildIdx.get(childIdx)
        const targetText =
          targetSpan?._type === 'span' && typeof targetSpan.text === 'string'
            ? targetSpan.text
            : undefined
        if (targetText == null) {
          localSliceByChild.set(childIdx, localCount++)
          texts.push(enText)
          return
        }
        if (targetText === enText) {
          const isDeliberate =
            typeof targetSpan?._key === 'string' &&
            typeof (child as { _key?: string })._key === 'string' &&
            targetSpan._key === (child as { _key: string })._key
          if (isDeliberate) {
            already.n++
          } else {
            localSliceByChild.set(childIdx, localCount++)
            texts.push(enText)
          }
          return
        }
        already.n++
      })

      plans.push({
        count: localCount,
        rebuild: (slice) => ({
          ...member,
          children: children.map((c, childIdx) => {
            if (c?._type !== 'span') return { ...c }
            const si = localSliceByChild.get(childIdx)
            if (si != null) return { ...c, text: slice[si] ?? c.text }
            const existing = alignedSpanByChildIdx.get(childIdx)
            if (existing?._type === 'span' && typeof existing.text === 'string') {
              return { ...c, text: existing.text }
            }
            return { ...c }
          }),
        }),
      })
      textBlockIdx++
    } else {
      const key = keyOf(member)
      const curMember = key != null ? currentByKey.get(key) : undefined
      const nested = collectNestedMissing(member, curMember, lang, already)
      plans.push({
        count: nested.texts.length,
        rebuild: (slice) => nested.rebuild(slice) as PortableBlock,
      })
      texts.push(...nested.texts)
    }
  }

  return {
    texts,
    rebuild: (translated) => rebuildFromPlans(plans, translated),
    translatedAlready: already.n,
  }
}

/**
 * Walk the English source and identify spans that are still untranslated
 * in the current target value. Top-level prose blocks (and spans within
 * them) align by `_key` first — our rebuild writes source keys into the
 * target, so inserting/deleting/reordering English paragraphs no longer
 * shifts every subsequent translation into the wrong block — with a
 * positional fallback for legacy imports whose keys don't match the
 * source (see `alignTextBlocks` / `alignChildSpans`).
 *
 * A span is "untranslated" when the aligned target span either doesn't
 * exist or carries the English source text verbatim AND its `_key` does
 * not match the source span's `_key` (our rebuild writes source `_key`s
 * into the target, so a match signals a deliberate prior no-op — e.g.
 * a URL DeepL returned unchanged — and should not be re-flagged).
 *
 * This applies at EVERY depth: nested `localePortableText` inside chapters
 * and step-by-step grids is aligned against ITS OWN `.<lang>` sub-array in
 * the target tree (nested structural members matched by `_key`), so an
 * untranslated step instruction is flagged (and later refilled) exactly like
 * an untranslated top-level paragraph — and an already-translated one is
 * left byte-for-byte. `lang` selects which nested sub-array is compared and
 * written.
 *
 * Returns the texts to send to DeepL plus a rebuilder that, when given
 * the translated chunks back, produces a NEW target body where ONLY the
 * flagged spans are replaced. Prior human edits / prior machine output
 * in the remaining spans are preserved verbatim.
 */
export function extractMissingSpans(
  source: PortableBlock[],
  current: PortableBlock[] | undefined,
  lang: string
): {
  texts: string[]
  rebuild: (translated: string[]) => PortableBlock[]
  translatedAlready: number
} {
  return collectMissingInBlocks(source, current, lang)
}

/** Flatten a portable-text array to plain text (paragraphs joined). */
export function portableTextToPlain(blocks: PortableBlock[]): string {
  const paragraphs: string[] = []
  for (const block of blocks) {
    if (block?._type !== 'block' || !Array.isArray(block.children)) continue
    const text = block.children
      .filter((c) => c?._type === 'span' && typeof c.text === 'string')
      .map((c) => c.text || '')
      .join('')
    if (text.trim()) paragraphs.push(text)
  }
  return paragraphs.join('\n\n')
}

/**
 * DEEP plain-text of a portable-text array: top-level prose PLUS the text
 * of every nested localePortableText (chapter content, step-by-step
 * stepInstructions/additionalComment, annotated-image annotations — the
 * same tree the missing-span walk recurses). `lang` selects which nested
 * sub-array to read (`'en'` for source hashing; the target language for
 * baseline snapshots of a rebuilt tree).
 *
 * This exists because `portableTextToPlain` reads only top-level blocks —
 * and 191 of 193 tutorials (plus every scene) keep ALL their text inside
 * chapter blocks, so their bodies flattened to '' and both the machine
 * baseline and the English-source staleness hash were structurally inert
 * for the site's primary content.
 */
export function portableTextToPlainDeep(
  blocks: PortableBlock[],
  lang: string
): string {
  const paragraphs: string[] = []

  const walkValue = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (const el of v) walkValue(el)
      return
    }
    if (!v || typeof v !== 'object') return
    const o = v as Record<string, unknown>
    if (o._type === 'block' && Array.isArray(o.children)) {
      const text = (o.children as PortableSpan[])
        .filter((c) => c?._type === 'span' && typeof c.text === 'string')
        .map((c) => c.text || '')
        .join('')
      if (text.trim()) paragraphs.push(text)
      return
    }
    if (isLocalePortableText(o)) {
      const sub = o[lang]
      if (Array.isArray(sub)) walkValue(sub)
      return
    }
    // Generic object (chapter, stepByStepGrid, step, annotation wrapper):
    // recurse into every property so nested localePortableText at any
    // depth is reached — mirrors collectNestedFull's walk.
    for (const value of Object.values(o)) walkValue(value)
  }

  walkValue(blocks)
  return paragraphs.join('\n\n')
}
